import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * NotFoundPage
 *
 * Sets a noindex meta + prerender-status-code 404 by directly mutating the
 * document head (we don't use react-helmet-async here because the app relies
 * on AutoSEO + React 19 native metadata; adding a HelmetProvider would
 * duplicate head management).
 */
export default function NotFoundPage() {
  useEffect(() => {
    // Flag the window so AutoSEO defers to us for this route.
    if (typeof window !== 'undefined') window.__ihgNotFound = true;

    document.title = 'Page Not Found | IHGIND';

    const upsert = (sel, attrs) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      return el;
    };

    const prevRobots = document.head.querySelector('meta[name="robots"]')?.getAttribute('content');
    upsert('meta[name="robots"]', { name: 'robots', content: 'noindex, nofollow' });
    upsert('meta[name="prerender-status-code"]', { name: 'prerender-status-code', content: '404' });

    // Remove any existing canonical — a 404 must not self-canonical
    const canon = document.head.querySelector('link[rel="canonical"]');
    const prevCanon = canon?.getAttribute('href');
    canon?.remove();

    return () => {
      // Clear the 404 flag so subsequent pages get normal AutoSEO behaviour.
      if (typeof window !== 'undefined') window.__ihgNotFound = false;
      // Restore previous robots content so navigating back doesn't noindex
      if (prevRobots) {
        upsert('meta[name="robots"]', { name: 'robots', content: prevRobots });
      }
      document.head.querySelector('meta[name="prerender-status-code"]')?.remove();
      // Restore canonical (best-effort — AutoSEO will set the right one on nav)
      if (prevCanon) {
        const link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        link.setAttribute('href', prevCanon);
        document.head.appendChild(link);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center max-w-md">
        <span className="text-8xl font-bold text-neutral-200">404</span>
        <h1 className="text-xl font-bold uppercase tracking-[0.15em] mt-4 mb-2">Page Not Found</h1>
        <p className="text-sm text-neutral-500 mb-8">The page you&apos;re looking for doesn&apos;t exist or has been moved.</p>
        <div className="flex flex-col gap-2 items-center">
          <Link
            to="/"
            data-testid="not-found-home-link"
            className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white text-xs font-bold uppercase tracking-[0.1em] hover:bg-neutral-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </Link>
          <div className="flex flex-wrap justify-center gap-3 text-xs text-neutral-500 mt-4">
            <Link to="/capabilities" className="hover:text-black underline-offset-2 hover:underline">Capabilities</Link>
            <Link to="/finishes" className="hover:text-black underline-offset-2 hover:underline">Finishes</Link>
            <Link to="/projects" className="hover:text-black underline-offset-2 hover:underline">Projects</Link>
            <Link to="/contact" className="hover:text-black underline-offset-2 hover:underline">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
